/* eslint-disable @typescript-eslint/no-explicit-any */
import { Route, Routes, useLocation } from 'react-router-dom';
import { ProtectedLayout } from './layout/ProtectedLayout';

import { UnprotectedLayout } from './layout/UnprotectedLayout';
import Login from './pages/login';
import Home from './pages/home';
import OnBoardingStep1 from './pages/on-boarding/step-1';
import OnBoardingStep2 from './pages/on-boarding/step-2';
import OnBoardingStep3 from './pages/on-boarding/step-3';
import OnBoardingStep4 from './pages/on-boarding/step-4';
import OnBoardingStep5 from './pages/on-boarding/step-5';
import DailyInsight from './pages/daily-insight';
import RegistrationForm from './pages/register';
import ResetPassword from './pages/reset-password/code';
import ForgotPassword from './pages/reset-password';
import DailyBubbleFinishedPage from './pages/daily-bubble-finish';
import KnowledgeHub from './pages/knowledge-hub/page';
import EmotionalWheelPage from './pages/emotion-wheel';
import MindfulMinutePage from './pages/mindful-minute';
import ProfilePage from './pages/profile';
import PodcastPage from './pages/podcast';
import PodcastDetail from './pages/podcast/detail';
import Courses from './pages/learn/courses';
import CoursesInProgress from './pages/learn/in-progress';
import CoursesCompleted from './pages/learn/completed';
import Course from './pages/learn/course';
import Lesson from './pages/learn/course-lesson';
import NotfoundPage from './pages/not-found';
import FavouritePage from './pages/favourites';
import withAuthGuard, { RoutesEnum } from './auth.guard';
import NotAuthorizedPage from './pages/not-authorized';
import CoursesSearch from './pages/learn/search';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import DashboardPage from './pages/admin/dashboard';
import { OnBoardingAdminStep1 } from './pages/admin/on-boarding-admin/step-1';
import { OnBoardingAdminStep2 } from './pages/admin/on-boarding-admin/step-2';
import { SchoolManagement } from './pages/admin/school-management';
import { OnboardingContainer } from './pages/on-boarding/container';
import AboutPageComponent from './pages/about-page';
import { SearchPage } from './pages/search';
import WellbeingAwarenessDaysCalendarPageComponent from './pages/wellbeing-awareness-days-calendar-page';
import TermsAndConditionsPageComponent from './pages/terms-and-conditions-page';
import PrivacyPolicyPageComponent from './pages/privacy-policy-page';
import CopyrightPageComponent from './pages/copyright-page';
import CookiesPageComponent from './pages/cookies-page';
import SurveyDashboard from './pages/admin/survey-dashboard';
import MeditationPage from './pages/meditation';
import MeditationDetailPage from './pages/meditation/detail';
import { FeedbackPage } from './pages/learn/feedback';
import { Announcements } from './pages/admin/announcements';
import SurveyMethodology from './pages/survey-methodology';

export function Router() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    const devDomains = ['dev.app.stillhuman.co.uk'];

    const isDevEnvironment = import.meta.env.MODE === 'development' || devDomains.includes(window.location.hostname);

    if (isDevEnvironment) {
      console.warn('GA has been disabled because this is a development environment.');
      return;
    }

    if (!import.meta.env.VITE_GA) {
      return;
    }

    ReactGA.initialize(import.meta.env.VITE_GA);
  }, []);

  return (
    <Routes>
      <Route path="/" element={<ProtectedLayout />}>
        <Route path="/" element={<Home />} />
        <Route element={<OnboardingContainer />}>
          <Route path="/on-boarding/step-1" element={<OnBoardingStep1 />} />
          <Route path="/on-boarding/step-2" element={<OnBoardingStep2 />} />
          <Route path="/on-boarding/step-3" element={<OnBoardingStep3 />} />
          <Route path="/on-boarding/step-4" element={<OnBoardingStep4 />} />
          <Route path="/on-boarding/step-5" element={<OnBoardingStep5 />} />
        </Route>

        <Route path="/admin-on-boarding/step-1" element={<OnBoardingAdminStep1 />} />
        <Route path="/admin-on-boarding/step-2" element={<OnBoardingAdminStep2 />} />
        <Route path="/school-management" element={<SchoolManagement />} />
        <Route path="/announcements" element={withAuthGuard(<Announcements />, RoutesEnum.ANNOUNCEMENTS)} />
        <Route path="/about" element={<AboutPageComponent />} />
        <Route path="/survey-methodology" element={<SurveyMethodology />} />
        <Route path="/cookies" element={<CookiesPageComponent />} />
        <Route path="/copyright" element={<CopyrightPageComponent />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPageComponent />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPageComponent />} />
        <Route path="/wellbeing-awareness-days-calendar" element={<WellbeingAwarenessDaysCalendarPageComponent />} />

        <Route path="/daily-insight" element={withAuthGuard(<DailyInsight />, RoutesEnum.DAILY_BUBBLE)} />
        <Route path="/emotion-wheel" element={withAuthGuard(<EmotionalWheelPage />, RoutesEnum.DAILY_BUBBLE)} />
        <Route path="/mindful-minute" element={withAuthGuard(<MindfulMinutePage />, RoutesEnum.DAILY_BUBBLE)} />
        <Route
          path="/daily-bubble-finished"
          element={withAuthGuard(<DailyBubbleFinishedPage />, RoutesEnum.DAILY_BUBBLE)}
        />

        <Route path="/knowledge-hub" element={withAuthGuard(<KnowledgeHub />, RoutesEnum.KNOWLEDGE_HUB)} />
        <Route path="/knowledge-hub/slug/:slug" element={withAuthGuard(<KnowledgeHub />, RoutesEnum.KNOWLEDGE_HUB)} />
        <Route path="/knowledge-hub/topic/:topic" element={withAuthGuard(<KnowledgeHub />, RoutesEnum.KNOWLEDGE_HUB)} />
        <Route
          path="/knowledge-hub/topic/:topic/theme/:theme"
          element={withAuthGuard(<KnowledgeHub />, RoutesEnum.KNOWLEDGE_HUB)}
        />

        <Route path="/mindful-exercise" element={<MeditationPage />} />
        <Route path="/mindful-exercise/:slug" element={<MeditationDetailPage />} />

        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/learn" element={withAuthGuard(<Courses />, RoutesEnum.LEARN)} />
        <Route path="/learn/search" element={withAuthGuard(<CoursesSearch />, RoutesEnum.LEARN)} />
        <Route path="/learn/in-progress" element={withAuthGuard(<CoursesInProgress />, RoutesEnum.LEARN)} />
        <Route path="/learn/completed" element={withAuthGuard(<CoursesCompleted />, RoutesEnum.LEARN)} />
        <Route path="/learn/course/:slug" element={withAuthGuard(<Course />, RoutesEnum.LEARN)} />
        <Route
          path="/learn/course/:slug/module/:idModule/lesson/:idLesson"
          element={withAuthGuard(<Lesson />, RoutesEnum.LEARN)}
        />
        <Route path="/learn/course/:slug/feedback" element={withAuthGuard(<FeedbackPage />, RoutesEnum.LEARN)} />
        <Route path="/podcast" element={withAuthGuard(<PodcastPage />, RoutesEnum.PODCAST)} />
        <Route path="/podcast/:slug" element={withAuthGuard(<PodcastDetail />, RoutesEnum.PODCAST)} />
        <Route path="/bookmarks" element={<FavouritePage />} />
        <Route path="/dashboard" element={withAuthGuard(<DashboardPage />, RoutesEnum.REPORTING_DASHBOARD)} />
        <Route path="/survey-dashboard" element={withAuthGuard(<SurveyDashboard />, RoutesEnum.SURVEY_DASHBOARD)} />
        <Route path="/admin" element={withAuthGuard(<SchoolManagement />, RoutesEnum.ADMIN_MANAGEMENT)} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="*" element={<NotfoundPage />} />
        <Route path="/not-authorized" element={<NotAuthorizedPage />} />
      </Route>
      <Route element={<UnprotectedLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/login/confirmed" element={<Login />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:code" element={<ResetPassword />} />
        <Route path="/register" element={<RegistrationForm />} />
        <Route path="/register/:code" element={<RegistrationForm />} />
      </Route>
    </Routes>
  );
}
